import React, { useEffect, useMemo, useRef, useState } from 'react'
import SocialButtons from './AccountAccessSocialButtons'
import TextInput from 'components/Common/Form/Input/TextInput'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import { checkEmail } from 'actions/AuthActions'
import TextButton from 'components/Luxkit/Button/TextButton'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { loginModalOpen } from 'analytics/eventDefinitions'
import { fireNonInteractionEvent } from 'api/googleTagManager'
import AccountAccessLegalText from './AccountAccessLegalText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import LineGiftIcon from 'components/Luxkit/Icons/line/LineGiftIcon'
import LineStarIcon from 'components/Luxkit/Icons/line/LineStarIcon'
import LinePhoneIcon from 'components/Luxkit/Icons/line/LinePhoneIcon'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import TrustIndicatorsMapper from './AccountAccessTrustIndicatorsMapper'
import LineHeartIcon from 'components/Luxkit/Icons/line/LineHeartIcon'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import LineUniversityIcon from 'components/Luxkit/Icons/line/LineUniversityIcon'
import noop from 'lib/function/noop'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { rem } from 'polished'
import styled from 'styled-components'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import * as Analytics from 'analytics/analytics'
import { clientEvent } from 'analytics/snowplow/events'

interface LoginForm {
  email: string;
}

const Or = styled.div`
  display: flex;
  align-items: center;

  &::after,
  &::before {
    content: '';
    flex: 1;
    border-top: 1px solid ${({ theme }) => theme.palette.neutral.five};
    display: block;
  }
`

const OrText = styled(BodyText)`
  margin: 0 ${rem(12)};
`

type LoginUIContent = {
  Icon: JSX.Element;
  text: string;
}[];

type LoginUIData = {
  content: LoginUIContent;
  title: string;
};

function getLoginUIData(mode?: App.UiAccountModalMode): LoginUIData {
  switch (mode) {
    case 'tripPlannerLogin':
      return {
        title: 'Sign up to start planning your next escape',
        content: [
          { Icon: <LineHeartIcon />, text: 'Save your favourite offers' },
          { Icon: <LineCalendarIcon />, text: 'Easily plan your perfect trip' },
          { Icon: <LineUniversityIcon />, text: 'Explore must-see attractions' },
        ],
      }
    default:
      return {
        title: 'Members save up to 70%',
        content: [
          { Icon: <LineGiftIcon />, text: 'Unlock thousands of exclusive offers' },
          { Icon: <LineStarIcon />, text: 'Over a million trips taken every year' },
          { Icon: <LinePhoneIcon />, text: 'Our local team are available 24 hours, 7 days' },
        ],
      }
  }
}

interface Props {
  error?: any;
  headlessMode?: boolean;
  isStoreMode: boolean;
  mode?: App.UiAccountModalMode
  onModeChange?: (mode: App.UiAccountModalMode) => void;
  onSubmit?: (email: string) => void;
}

function AccountAccessLoginStart(props: Props) {
  const {
    error,
    headlessMode,
    isStoreMode,
    mode,
    onModeChange = noop,
    onSubmit = noop,
  } = props

  const formRef = useRef<HTMLFormElement>(null)
  const emailInputRef = useRef<HTMLInputElement>(null)
  const isMobile = useIsMobileScreen()
  const [checkingEmail, setCheckingEmail] = useState<string | undefined>()
  const processing = useAppSelector(state => state.auth.processing)
  const userExists = useAppSelector(state => !!state.auth.emailsChecked[checkingEmail!])

  useEffect(() => {
    if (checkingEmail && !processing) {
      onModeChange(userExists ? 'loginPassword' : 'join')
      setCheckingEmail(undefined)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkingEmail, processing])

  useEffect(() => {
    Analytics.track('client-event', clientEvent('sign_up_appear', 'impression', 'logging', 'operational'))
    if (!headlessMode && !isMobile) {
      emailInputRef.current?.focus()
    }
    fireNonInteractionEvent(loginModalOpen())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dispatch = useAppDispatch()

  const reRef = useRef<ReCAPTCHA>(null)

  const onCheckEmail = async(e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const loginData = formToObject<LoginForm>(e.currentTarget)
    setCheckingEmail(loginData.email)
    onSubmit(loginData.email)
    dispatch(checkEmail(loginData.email, reRef.current ?? undefined))
  }

  const socialMediaLoginsEnabled = config.FACEBOOK_APP_ID || config.GOOGLE_APP_ID || config.APPLE_APP_ID
  const showSocialMediaButtons = !isStoreMode && socialMediaLoginsEnabled
  const { title, content } = useMemo(() => getLoginUIData(mode), [mode])

  return (<>
    <ModalHeader title={title} />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={24}>
          <VerticalSpacer ref={formRef} gap={24} as="form" onSubmit={onCheckEmail} name="loginForm">
            <Group direction="vertical" gap={20}>
              {error?.message && <MessageBanner kind="critical" description={error.message} />}
              <Group direction="vertical" gap={4}>
                {content.map(({ Icon, text }) => <BodyTextBlock
                  key={text}
                  variant="medium"
                  startIcon={Icon}
                >
                  {text}
                </BodyTextBlock>)}
              </Group>
              {config.LOGIN_TRUST_INDICATORS && <Group direction="horizontal" horizontalAlign="center">
                <TrustIndicatorsMapper />
              </Group>}
            </Group>
            <Group direction="vertical" gap={32} tabletGap={24}>
              <TextInput
                label="Email address"
                id="email"
                required
                type="email"
                name="email"
                autoComplete="email"
                placeholder="Email"
                requiredErrorMessage="Email is required"
                ref={emailInputRef}
                helpText="We'll check if you have an account with us or if we need to create one for you."
              />
              <TextButton
                kind="primary"
                size="large"
                type="submit"
              >
                Continue
              </TextButton>
            </Group>
            {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reRef} />}
          </VerticalSpacer>
          {showSocialMediaButtons && <>
            <Or>
              <OrText colour="neutral-two" variant="medium">Other ways to log in</OrText>
            </Or>
            <SocialButtons mode="login" renderId="accountAccessModal" inline/>
          </>}
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
    <ModalFooter>
      <AccountAccessLegalText />
    </ModalFooter>
  </>)
}

function mapStateToProps(state: App.State) {
  return {
    error: state.auth.error,
    headlessMode: state.config.headlessMode,
    isStoreMode: state.config.storeMode,
  }
}

export default connect(mapStateToProps)(AccountAccessLoginStart)
